import styles from './BulkUpdateVersionModal.module.scss';
import { useScopedTranslation } from 'application/localization/useScopedTranslation.ts';
import { useCallback, useMemo, useState } from 'react';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm.ts';
import { QueryFactory } from 'services/api';
import { CustomModal } from 'components/uikit/modal/CustomModal.tsx';
import { Loading } from 'components/uikit/suspense/Loading.tsx';
import { RadioButtonGroup } from 'components/uikit/RadioButtonGroup.tsx';
import { Field } from 'components/uikit/Field.tsx';
import { Button, ButtonColor } from 'components/uikit/buttons/Button.tsx';
import { Input } from 'components/uikit/inputs/Input.tsx';
import { useSelectModeContext } from '../../useSelectModeContext.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { requiredRule } from 'helpers/form/react-hook-form-helper.ts';
import { FileNodeState } from '../../SelectionStore.ts';
import { showErrorToast } from 'components/uikit/toast/showToast.tsx';
import { useVariablesContext } from 'pages/authorized/variables/variablesContext/VariablesContext.tsx';

enum UpdateType {
  Increment,
  SetSpecific,
}

type UpdateTypeRadioButtonOption = { text: string; value: UpdateType };

type BulkUpdateVersionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  revisionId: number;
  selectedDocuments: FileNodeState[];
};

export const BulkUpdateVersionModal = (props: BulkUpdateVersionModalProps) => {
  const queryClient = useQueryClient();
  const i18n = useScopedTranslation('Component.BulkUpdateVersionModal');
  const { setIsSelectMode } = useSelectModeContext();
  const { refreshVariables } = useVariablesContext();

  const [isLoading, setIsLoading] = useState(false);
  const [updateVersionType, setUpdateVersionType] = useState(
    UpdateType.Increment,
  );

  const form = useAdvancedForm<{
    version?: string;
  }>(
    useCallback(
      async (data) => {
        setIsLoading(true);

        try {
          const patchEntries = props.selectedDocuments.map((d) => ({
            id: d.documentId,
            patchDto: {
              versionEntry: {
                autoIncrementVersion:
                  updateVersionType === UpdateType.Increment,
                version: data.version ?? null,
              },
            },
          }));
          await QueryFactory.DocumentClient.deferBulkPatch(patchEntries);
        } catch (e) {
          showErrorToast(e);
        }

        await onModalClose();

        setIsLoading(false);
        setIsSelectMode(false);
      },
      [updateVersionType, props.selectedDocuments],
    ),
  );

  const onModalClose = async () => {
    await queryClient.invalidateQueries({
      queryKey: QueryFactory.DocumentQuery.searchQueryKey(),
    });
    await refreshVariables();

    form.reset();
    form.clearErrors();
    props.onClose();
  };

  const versionUpdateTypeOptions: UpdateTypeRadioButtonOption[] = useMemo(
    () => [
      { text: i18n.t('increment_type'), value: UpdateType.Increment },
      { text: i18n.t('set_specific_type'), value: UpdateType.SetSpecific },
    ],
    [i18n],
  );

  return (
    <CustomModal
      isOpen={props.isOpen}
      onClose={onModalClose}
      title={i18n.t('title')}
      isBlocking
    >
      <Loading loading={isLoading}>
        <form onSubmit={form.handleSubmitDefault}>
          <Field title={i18n.t('update_type_field')} className={styles.field}>
            <RadioButtonGroup<UpdateTypeRadioButtonOption, 'value'>
              options={versionUpdateTypeOptions}
              valueField={'value'}
              labelField={'text'}
              onSelectedOptionChanged={(option) =>
                setUpdateVersionType(option.value)
              }
              rootClassName={styles.radioButtonGroup}
              value={updateVersionType}
            />
          </Field>
          {updateVersionType === UpdateType.SetSpecific && (
            <Field title={i18n.t('set_specific_type')} className={styles.field}>
              <Input
                {...form.register('version', { ...requiredRule() })}
                errorText={form.formState.errors.version?.message}
              />
            </Field>
          )}
          <div className={styles.footer}>
            <Button
              title={i18n.t('cancel_button')}
              className={styles.button}
              color={ButtonColor.Secondary}
              onClick={onModalClose}
            />
            <Button
              title={i18n.t('ok_button')}
              className={styles.button}
              type={'submit'}
            />
          </div>
        </form>
      </Loading>
    </CustomModal>
  );
};
